import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// Hooks
import { useAppDispatch } from '../../misc/hooks'

// Actions
// abtestDuplicate(id) et abtestDelete(id) : à ajouter dans le slice
import { getInitialState, abtestCreate, abtestGetAll, abtestDuplicate, abtestDelete } from '../../slice/abtest'

// ---------- Config ----------

const PAGE_SIZE = 20

type Status = 'live' | 'scheduled' | 'paused' | 'draft' | 'completed'
type Tab = 'all' | Status
type Sort = 'smart' | 'updated' | 'end' | 'name'

const TABS: { key: Tab, label: string }[] = [
	{ key: 'all', label: 'All' },
	{ key: 'live', label: 'Live' },
	{ key: 'scheduled', label: 'Scheduled' },
	{ key: 'paused', label: 'Paused' },
	{ key: 'draft', label: 'Drafts' },
	{ key: 'completed', label: 'Completed' },
]

const STATUS_LABEL: Record<Status, string> = {
	live: 'Live',
	scheduled: 'Scheduled',
	paused: 'Paused',
	draft: 'Draft',
	completed: 'Completed',
}

// Ordre du tri "smart" : les tests actifs d'abord
const STATUS_ORDER: Record<string, number> = { live: 0, scheduled: 1, paused: 2, draft: 3, completed: 4 }

const EMPTY_TAB_MESSAGE: Record<Tab, string> = {
	all: 'No test matches your search.',
	live: 'No test is live right now.',
	scheduled: 'No test is scheduled.',
	paused: 'No paused test.',
	draft: 'No draft. Every test you start is saved here until you launch it.',
	completed: 'No completed test yet. Results of finished tests will show up here.',
}
// ---------- Helpers ----------

const formatDate = (date: any) => {
	if (!date) return ''
	try {
		return new Intl.DateTimeFormat(navigator?.language || 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date))
	} catch (e) {
		return ''
	}
}

const time = (date: any) => (date ? new Date(date).getTime() : 0)

// Date la plus utile selon le statut
const getDateInfo = (t: any): string => {
	switch (t?.status) {
		case 'live': return t.end_at ? `Ends ${formatDate(t.end_at)}` : 'Running'
		case 'scheduled': return t.scheduled_at ? `Starts ${formatDate(t.scheduled_at)}` : ''
		case 'paused': return t.end_at ? `Planned end ${formatDate(t.end_at)}` : ''
		case 'completed': return t.end_at ? `Ended ${formatDate(t.end_at)}` : ''
		default: return ''
	}
}

const smartCompare = (a: any, b: any) => {
	const rank = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5)
	if (rank !== 0) return rank
	// live : se termine bientôt en premier / scheduled : démarre bientôt en premier
	if (a.status === 'live') return time(a.end_at) - time(b.end_at)
	if (a.status === 'scheduled') return time(a.scheduled_at) - time(b.scheduled_at)
	// le reste : dernière modif en premier
	return time(b.updated_at) - time(a.updated_at)
}

function Dashboard() {
	const dispatch = useAppDispatch()
	const history = useHistory();
	const [abtestList, setAbtestList] = useState<any[]>([])
	const [isLoadingList, setIsLoadingList] = useState<boolean>(true)
	const [isLoadingCreate, setIsLoadingCreate] = useState<boolean>(false)

	const [tab, setTab] = useState<Tab>('all')
	const [search, setSearch] = useState<string>('')
	const [sort, setSort] = useState<Sort>('smart')
	const [page, setPage] = useState<number>(1)

	// Menu ⋯ d'une ligne
	const [menu, setMenu] = useState<{ id: any, top?: number, bottom?: number, right: number } | null>(null)
	const [confirmDeleteId, setConfirmDeleteId] = useState<any>(null)
	const [loadingDuplicateId, setLoadingDuplicateId] = useState<any>(null)
	const [loadingDeleteId, setLoadingDeleteId] = useState<any>(null)

	useEffect(() => {
		const init = async () => {
			// load the abtest
			let res: any = await dispatch(abtestGetAll())

			setIsLoadingList(false)

			if (res?.payload?.length) {
				setAbtestList(res.payload)
			}
		}

		init()
	}, [dispatch]);

	// Retour à la page 1 dès qu'on change de filtre
	useEffect(() => { setPage(1) }, [tab, search, sort])

	// Fermeture du menu : clic à l'extérieur, Échap, scroll, resize
	useEffect(() => {
		if (!menu) return
		const onMouseDown = (e: MouseEvent) => {
			if (!(e.target as HTMLElement)?.closest?.('[data-row-menu]')) closeMenu()
		}
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu() }
		document.addEventListener('mousedown', onMouseDown)
		document.addEventListener('keydown', onKey)
		window.addEventListener('scroll', closeMenu, true)
		window.addEventListener('resize', closeMenu)
		return () => {
			document.removeEventListener('mousedown', onMouseDown)
			document.removeEventListener('keydown', onKey)
			window.removeEventListener('scroll', closeMenu, true)
			window.removeEventListener('resize', closeMenu)
		}
	}, [menu])

	const counts = useMemo(() => {
		const c: Record<string, number> = { all: abtestList.length }
		abtestList.forEach((t: any) => { c[t.status] = (c[t.status] || 0) + 1 })
		return c
	}, [abtestList])

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		const list = abtestList
			.filter((t: any) => tab === 'all' || t.status === tab)
			.filter((t: any) => !q || (t.name || '').toLowerCase().includes(q))

		return [...list].sort((a: any, b: any) => {
			switch (sort) {
				case 'updated': return time(b.updated_at) - time(a.updated_at)
				case 'end': return (time(a.end_at) || Infinity) - (time(b.end_at) || Infinity)
				case 'name': return (a.name || '').localeCompare(b.name || '')
				default: return smartCompare(a, b)
			}
		})
	}, [abtestList, tab, search, sort])

	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
	const currentPage = Math.min(page, pageCount)
	const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
	const from = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0
	const to = Math.min(currentPage * PAGE_SIZE, filtered.length)

	const handleCreateAbtest = async () => {
		if (isLoadingCreate) return
		setIsLoadingCreate(true)
		let res = await dispatch(abtestCreate(getInitialState()))
		setIsLoadingCreate(false)
		if (res?.payload?.id) {
			history.push(`/abtest/edit/${res.payload.id}`);
		}
	}

	const openTest = (id: any) => history.push(`/abtest/edit/${id}`)

	const closeMenu = () => {
		setMenu(null)
		setConfirmDeleteId(null)
	}

	// Ouvre le menu en position fixe (le tableau a overflow-x auto, un menu absolu serait coupé)
	const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>, id: any) => {
		e.stopPropagation()
		if (menu?.id === id) return closeMenu()

		const rect = e.currentTarget.getBoundingClientRect()
		const MENU_HEIGHT = 160
		const right = window.innerWidth - rect.right
		setConfirmDeleteId(null)

		// Pas assez de place en dessous : on l'ouvre vers le haut
		if (rect.bottom + MENU_HEIGHT > window.innerHeight) {
			setMenu({ id, bottom: window.innerHeight - rect.top + 6, right })
		} else {
			setMenu({ id, top: rect.bottom + 6, right })
		}
	}

	const handleDuplicateAbtest = async (id: any) => {
		if (loadingDuplicateId) return
		setLoadingDuplicateId(id)
		let res = await dispatch(abtestDuplicate({abtest_id: id}))
		setLoadingDuplicateId(null)
		closeMenu()
		if (res?.payload?.id) {
			history.push(`/abtest/edit/${res.payload.id}`);
		}
	}

	const handleDeleteAbtest = async (id: any) => {
		if (loadingDeleteId) return

		// 1er clic : demande de confirmation
		if (confirmDeleteId !== id) {
			setConfirmDeleteId(id)
			return
		}

		// 2e clic : suppression
		setLoadingDeleteId(id)
		let res = await dispatch(abtestDelete({abtest_id: id}))
		
		if (res?.payload?.deleted === true)  {
			setLoadingDeleteId(null)
			setAbtestList((list) => list.filter((t: any) => t.id !== id))
			closeMenu()
		}
		
	}

	const deleteLabel = (id: any) => {
		if (loadingDeleteId === id) return 'Deleting…'
		if (confirmDeleteId === id) return 'Click again to confirm'
		return 'Delete'
	}

	const createButton = (label: string, big?: boolean) => (
		<button
			className={`dashboard__btn-create ${big ? 'big' : ''}`}
			onClick={() => handleCreateAbtest()}
			disabled={isLoadingCreate}
		>
			{label}
			{isLoadingCreate ?
				<span className="dashboard__load">
					<span className="dashboard__loader"></span>
				</span>
			: ''}
		</button>
	)

	// ---------- Loading ----------
	if (isLoadingList) {
		return (
			<div className="dashboard">
				<h2 className="dashboard__title">Your tests</h2>
				<div className="dashboard__load page">
					<div className="dashboard__loader"></div>
				</div>
			</div>
		)
	}

	// ---------- Empty state (new user) ----------
	if (!abtestList.length) {
		return (
			<div className="dashboard">

				<div className="dashboard__welcome">
					<svg className="dashboard__welcome-illu" viewBox="0 0 300 200" role="img" aria-label="The same product page with two different images, labelled A and B">
						<rect className="page-a" x="10" y="20" width="125" height="160" rx="12" />
						<rect className="img-a" x="25" y="35" width="95" height="70" rx="8" />
						<rect className="line-a" x="25" y="117" width="70" height="8" rx="4" />
						<rect className="line-a" x="25" y="133" width="45" height="8" rx="4" />
						<rect className="btn-a" x="25" y="152" width="95" height="16" rx="6" />

						<rect className="page-b" x="165" y="20" width="125" height="160" rx="12" />
						<rect className="img-b" x="180" y="35" width="95" height="70" rx="8" />
						<rect className="line-b" x="180" y="117" width="70" height="8" rx="4" />
						<rect className="line-b" x="180" y="133" width="45" height="8" rx="4" />
						<rect className="btn-b" x="180" y="152" width="95" height="16" rx="6" />

						<circle className="badge-a" cx="72" cy="20" r="14" />
						<text className="label-a" x="72" y="25" textAnchor="middle">A</text>
						<circle className="badge-b" cx="227" cy="20" r="14" />
						<text className="label-b" x="227" y="25" textAnchor="middle">B</text>
						<text className="vs" x="150" y="106" textAnchor="middle">vs</text>
					</svg>

					<div className="dashboard__welcome-content">
						<h3 className="dashboard__welcome-title">Welcome! Let's run your first A/B test</h3>

						<ol className="dashboard__welcome-steps">
							<li>
								<span className="dashboard__welcome-step-num">1</span>
								<span><strong>Pick a product page</strong><br/> One with steady traffic works best</span>
							</li>
							<li>
								<span className="dashboard__welcome-step-num">2</span>
								<span><strong>Change the images</strong> <br/> The main product image, the order of your images, or your whole gallery</span>
							</li>
							<li>
								<span className="dashboard__welcome-step-num">3</span>
								<span><strong>Launch and let your visitors decide</strong><br/> Follow the results live on your test’s page</span>
							</li>
						</ol>

						{createButton('Create my first A/B test', true)}
					</div>
				</div>
			</div>
		)
	}

	// ---------- List ----------
	return (
		<div className="dashboard">
			<div className="dashboard__header">
				<h2 className="dashboard__title">Your tests</h2>
				{createButton('+ Create A/B test')}
			</div>

			{/* Status tabs */}
			<div className="dashboard__tabs" role="tablist">
				{TABS.map((t) => (
					<button
						key={t.key}
						role="tab"
						aria-selected={tab === t.key}
						className={`dashboard__tab ${tab === t.key ? 'active' : ''}`}
						onClick={() => setTab(t.key)}
					>
						{t.label}
						<span className={`dashboard__tab-count ${t.key}`}>{counts[t.key] || 0}</span>
					</button>
				))}
			</div>

			{/* Search + sort */}
			<div className="dashboard__toolbar">
				<label className="dashboard__search">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
						<circle cx="11" cy="11" r="7"></circle>
						<path d="M20 20l-3.5-3.5"></path>
					</svg>
					<input
						type="search"
						placeholder="Search a test"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						aria-label="Search a test"
					/>
				</label>

				<label className="dashboard__sort">
					Sort by
					<select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
						<option value="smart">Smart (active first)</option>
						<option value="updated">Last updated</option>
						<option value="end">Ending soonest</option>
						<option value="name">Name</option>
					</select>
				</label>
			</div>

			{/* Table */}
			<div className="dashboard__container">
				<div className="dashboard__table">
					<div className="dashboard__table-row title">
						<div className="dashboard__table-cell">Name</div>
						<div className="dashboard__table-cell">Status</div>
						<div className="dashboard__table-cell">Dates</div>
						<div className="dashboard__table-cell">Updated</div>
						<div className="dashboard__table-cell"></div>
					</div>

					{pageItems.length ?
						pageItems.map((v: any) => (
							<div
								className={`dashboard__table-row ${menu?.id === v.id ? 'menu-open' : ''}`}
								key={v.id}
								role="link"
								tabIndex={0}
								onClick={() => openTest(v.id)}
								onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) openTest(v.id) }}
							>
								<div className={`dashboard__table-cell name ${v?.name ? '' : 'untitled'}`}>
									{v?.name || 'Untitled test'}
								</div>
								<div className="dashboard__table-cell">
									<span className={`dashboard__status ${v?.status || ''}`}>
										{STATUS_LABEL[v?.status as Status] || v?.status || ''}
									</span>
								</div>
								<div className="dashboard__table-cell muted">
									{getDateInfo(v) || '—'}
								</div>
								<div className="dashboard__table-cell muted">
									{formatDate(v?.updated_at)}
								</div>
								<div className="dashboard__table-cell">
									<button
										type="button"
										data-row-menu
										className={`dashboard__kebab ${menu?.id === v.id ? 'active' : ''}`}
										aria-label="More actions"
										aria-haspopup="menu"
										aria-expanded={menu?.id === v.id}
										onClick={(e) => toggleMenu(e, v.id)}
										onKeyDown={(e) => e.stopPropagation()}
									>
										<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
											<circle cx="5" cy="12" r="1.8"></circle>
											<circle cx="12" cy="12" r="1.8"></circle>
											<circle cx="19" cy="12" r="1.8"></circle>
										</svg>
									</button>
								</div>
							</div>
						))
					:
						<div className="dashboard__table-empty">
							<p>{search ? 'No test matches your search.' : EMPTY_TAB_MESSAGE[tab]}</p>
							{search ?
								<button className="dashboard__link" onClick={() => setSearch('')}>Clear search</button>
							: tab !== 'all' ?
								<button className="dashboard__link" onClick={() => setTab('all')}>See all tests</button>
							: ''}
						</div>
					}
				</div>
			</div>

			{/* Row menu */}
			{menu ?
				<div
					className="dashboard__menu"
					role="menu"
					data-row-menu
					style={{ top: menu.top, bottom: menu.bottom, right: menu.right }}
				>
					<button type="button" role="menuitem" className="dashboard__menu-item" onClick={() => openTest(menu.id)}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M9 6l6 6-6 6"></path>
						</svg>
						Open test
					</button>

					<button
						type="button"
						role="menuitem"
						className="dashboard__menu-item"
						onClick={() => handleDuplicateAbtest(menu.id)}
						disabled={!!loadingDuplicateId}
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<rect x="9" y="9" width="12" height="12" rx="2"></rect>
							<path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"></path>
						</svg>
						{loadingDuplicateId === menu.id ? 'Duplicating…' : 'Duplicate'}
					</button>

					<div className="dashboard__menu-divider"></div>

					<button
						type="button"
						role="menuitem"
						className={`dashboard__menu-item delete `}
						onClick={() => handleDeleteAbtest(menu.id)}
						disabled={!!loadingDeleteId}
						aria-live="polite"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"></path>
						</svg>
						{deleteLabel(menu.id)}
					</button>
				</div>
			: ''}

			{/* Pagination */}
			{filtered.length > PAGE_SIZE ?
				<div className="dashboard__pagination">
					<span className="dashboard__pagination-info">{from}–{to} of {filtered.length} tests</span>
					<div className="dashboard__pagination-pages">
						<button
							className="dashboard__page"
							onClick={() => setPage(currentPage - 1)}
							disabled={currentPage === 1}
							aria-label="Previous page"
						>‹</button>
						{Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
							<button
								key={p}
								className={`dashboard__page ${p === currentPage ? 'active' : ''}`}
								onClick={() => setPage(p)}
								aria-current={p === currentPage ? 'page' : undefined}
							>{p}</button>
						))}
						<button
							className="dashboard__page"
							onClick={() => setPage(currentPage + 1)}
							disabled={currentPage === pageCount}
							aria-label="Next page"
						>›</button>
					</div>
				</div>
			: ''}
		</div>
	);
}

export default Dashboard;